import { labelFor, labelForExact, lineColorOrder, normalizeHex } from '../model/lineColors.ts';
import type { LegendLabel } from '../model/types.ts';
import { formatLength, pricePerUnitValue, type Units } from '../units/units.ts';
import { runLengthFt, type Scene } from './scene.ts';

/**
 * Pricing / legend math. Ported verbatim from the original `render/draw.ts`
 * (`linesStrokeWidth`, `legendHeight`, `legendRows`, `legendTotal`, `formatMoney`,
 * `saturationMatrix`, `GUIDE_COLOR`) with the FIXED legend behaviors (07 §B9, §B33) as the
 * defaults and the originals as `*Original`; `legendStrip` extracts the text layout from
 * `drawLegend` (Skia drawing stays per platform). The original's `cached(map, key, make)`
 * helper is inlined as a Map lookup.
 */

export function linesStrokeWidth(scene: Pick<Scene, 'width'>): number {
  return Math.max(3, scene.width * 0.0042);
}

/** Height of the legend strip appended to the lines export. */
export function legendHeight(scene: Pick<Scene, 'width'>, rows: number): number {
  const unit = Math.max(14, scene.width * 0.016);
  return Math.round(unit * (3.2 + rows * 1.9));
}

export interface LegendRow {
  color: string;
  label: LegendLabel;
  feet: number;
  runs: number;
  lights: number;
}

/**
 * One row per line color in use, in palette order: the legend is exactly what was drawn.
 * FIXED behavior (known issue 07 §B9): line colors group case-insensitively (row color = the
 * uppercase hex) and labels are found case-insensitively. Original: `legendRowsOriginal`.
 */
export function legendRows(scene: Scene, labels: Record<string, LegendLabel>): LegendRow[] {
  return rowsBy(scene, labels, normalizeHex, labelFor);
}

/** ORIGINAL `legendRows`: exact-string grouping and label lookup. */
export function legendRowsOriginal(scene: Scene, labels: Record<string, LegendLabel>): LegendRow[] {
  return rowsBy(scene, labels, (hex) => hex, labelForExact);
}

function rowsBy(
  scene: Scene,
  labels: Record<string, LegendLabel>,
  key: (hex: string) => string,
  lookup: (labels: Record<string, LegendLabel>, hex: string) => LegendLabel,
): LegendRow[] {
  const rows = new Map<string, LegendRow>();
  for (const run of scene.runs) {
    const color = key(run.lineColor);
    let row = rows.get(color);
    if (row === undefined) {
      row = { color, label: lookup(labels, color), feet: 0, runs: 0, lights: 0 };
      rows.set(color, row);
    }
    row.feet += runLengthFt(run, scene.pxPerFt);
    row.runs += 1;
    row.lights += run.lights.length;
  }
  return [...rows.values()].sort((a, b) => lineColorOrder(a.color) - lineColorOrder(b.color));
}

/** Total price of priced rows (rows without a price are skipped). */
export function legendTotal(rows: LegendRow[]): number | null {
  const priced = rows.filter((r) => r.label.pricePerFt != null);
  return priced.length ? priced.reduce((sum, r) => sum + (r.label.pricePerFt ?? 0) * r.feet, 0) : null;
}

/** Whole currency units with en-US grouping, e.g. "$1,234" (totals and subtotals). */
export function formatMoney(value: number, currency: string): string {
  return `${currency}${Math.round(value).toLocaleString('en-US')}`;
}

/**
 * Per-foot prices. FIXED behavior (known issue 07 §B33): fractional prices keep their cents
 * ("$28.50", "$1,234.05"); whole prices stay whole ("$28"). cents = Math.round(value·100);
 * cents divisible by 100 → formatMoney(value). The original used formatMoney (whole units).
 */
export function formatPrice(value: number, currency: string): string {
  const cents = Math.round(value * 100);
  if (cents % 100 === 0) return formatMoney(value, currency);
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toLocaleString('en-US');
  return `${currency}${cents < 0 ? '-' : ''}${whole}.${String(abs % 100).padStart(2, '0')}`;
}

/** Text the legend strip draws (see `drawLegend` in the original), without fonts or positions. */
export interface LegendStripText {
  title: string;
  rows: { color: string; label: string; right: string }[];
  /** "Estimated total $N", drawn only when at least one row is priced AND lengths are shown. */
  total: string | null;
}

/**
 * Strip text; per-foot prices use `formatPrice` (cents when fractional), subtotals and total
 * whole units. With `units` (default imperial) lengths and prices follow docs/spec/09: "≈82 ft"
 * for estimates, "96 ft" for measured rows (`measured: true`), "$91.86/m" in metric.
 */
export function legendStrip(rows: (LegendRow & { measured?: boolean })[], currency: string, showLengths: boolean, units: Units = 'imperial'): LegendStripText {
  return stripWith(rows, currency, showLengths, formatPrice, units);
}

/** ORIGINAL strip text: per-foot prices in whole units too. */
export function legendStripOriginal(rows: LegendRow[], currency: string, showLengths: boolean): LegendStripText {
  return stripWith(rows, currency, showLengths, formatMoney);
}

function stripWith(
  rows: (LegendRow & { measured?: boolean })[],
  currency: string,
  showLengths: boolean,
  price: (v: number, c: string) => string,
  units: Units = 'imperial',
): LegendStripText {
  let total = 0;
  let priced = false;
  const out = rows.map((row) => {
    const label = row.label.product ? `${row.label.name} — ${row.label.product}` : row.label.name;
    const parts: string[] = [];
    if (showLengths) parts.push(formatLength(row.feet, units, { approx: !row.measured }));
    if (row.label.pricePerFt != null) {
      parts.push(`${price(pricePerUnitValue(row.label.pricePerFt, units), currency)}/${units === 'imperial' ? 'ft' : 'm'}`);
      if (showLengths) {
        const sub = row.label.pricePerFt * row.feet;
        total += sub;
        priced = true;
        parts.push(formatMoney(sub, currency));
      }
    }
    return { color: row.color, label, right: parts.join('   ·   ') };
  });
  return { title: 'Lighting layout', rows: out, total: priced ? `Estimated total ${formatMoney(total, currency)}` : null };
}

/** Color matrix for the muted photo in the Lines view (applied to 8-bit sRGB values). */
export function saturationMatrix(s: number, brightness: number): number[] {
  const lr = 0.2126 * (1 - s);
  const lg = 0.7152 * (1 - s);
  const lb = 0.0722 * (1 - s);
  const b = brightness;
  // prettier-ignore
  return [
    (lr + s) * b, lg * b, lb * b, 0, 0,
    lr * b, (lg + s) * b, lb * b, 0, 0,
    lr * b, lg * b, (lb + s) * b, 0, 0,
    0, 0, 0, 1, 0,
  ];
}

export const GUIDE_COLOR = '#FF00FF';

/* ------------------------------------------------------------------ measured lengths */

/** A legend row with the measured length applied (docs/spec/09 §1). */
export interface MeasuredLegendRow extends LegendRow {
  /** Effective length in feet: measuredFt[color] when present, else the photo estimate. */
  feet: number;
  /** The photo estimate (Σ run lengths / pxPerFt), always. */
  estimatedFeet: number;
  /** True when `feet` comes from measuredFt. */
  measured: boolean;
  /** feet × pricePerFt, or null when unpriced. */
  subtotal: number | null;
}

/**
 * `legendRows` with measured lengths: rows (case-insensitive grouping, palette order) whose
 * uppercase hex has a finite, positive `measuredFt` entry use it as `feet`; light counts always
 * come from the drawn runs. Keys of measuredFt are matched case-insensitively.
 */
export function measuredLegendRows(scene: Scene, labels: Record<string, LegendLabel>, measuredFt: Record<string, number> | null | undefined): MeasuredLegendRow[] {
  const measured: Record<string, number> = {};
  for (const [k, v] of Object.entries(measuredFt ?? {})) if (typeof v === 'number' && Number.isFinite(v) && v > 0) measured[normalizeHex(k)] = v;
  return legendRows(scene, labels).map((r) => {
    const m = measured[r.color];
    const feet = m ?? r.feet;
    return { ...r, feet, estimatedFeet: r.feet, measured: m !== undefined, subtotal: r.label.pricePerFt != null ? r.label.pricePerFt * feet : null };
  });
}

/** Drops measuredFt entries whose color no longer has a legend row (on save); undefined when empty. */
export function pruneMeasuredFt(measuredFt: Record<string, number> | null | undefined, runs: { lineColor: string }[]): Record<string, number> | undefined {
  const used = new Set(runs.map((r) => normalizeHex(r.lineColor)));
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(measuredFt ?? {})) if (used.has(normalizeHex(k))) out[normalizeHex(k)] = v;
  return Object.keys(out).length ? out : undefined;
}
